import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";

export type MatchupPrediction = {
  winnerTeamCode: string;
  gamesWon: number;
  bestThirdRank?: number;
};

type ScenarioPredictionItem = {
  seriesCode: string;
  winnerTeamCode: string;
  gamesWon: number;
  bestThirdRank?: number;
};

type ScenarioResponse = {
  scenarioId: number;
  poolCode: string;
  predictions: ScenarioPredictionItem[];
  updatedAt: string;
};

type UseScenarioArgs = {
  poolCode: string | null;
  enabled: boolean;
};

const toPredictionMap = (data: ScenarioResponse | null): Map<string, MatchupPrediction> | null => {
  if (!data) return null;
  const predictions = new Map<string, MatchupPrediction>();
  data.predictions.forEach((pred) => {
    predictions.set(pred.seriesCode, {
      winnerTeamCode: pred.winnerTeamCode,
      gamesWon: pred.gamesWon,
      ...(pred.bestThirdRank ? { bestThirdRank: pred.bestThirdRank } : {}),
    });
  });
  return predictions;
};

export function useScenario({ poolCode, enabled }: UseScenarioArgs) {
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const canLoad = Boolean(enabled && isAuthenticated && poolCode);
  const [error, setError] = useState<string | null>(null);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>("");

  const scenarioQuery = useQuery({
    queryKey: ["scenario", poolCode ?? "none"],
    enabled: false,
    queryFn: async (): Promise<ScenarioResponse | null> => {
      if (!canLoad || !poolCode) return null;

      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required.");
      }

      const url = apiUrl(`/api/scenarios?pool_code=${encodeURIComponent(poolCode)}`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to load scenario (${response.status})`);
      }

      const data: ScenarioResponse | null = await response.json();
      return data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (predictionsArray: ScenarioPredictionItem[]) => {
      if (!canLoad || !poolCode) return;

      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(apiUrl("/api/scenarios"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          poolCode,
          predictions: predictionsArray,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save scenario (${response.status})`);
      }
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Unknown error");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!canLoad || !poolCode) return;

      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required.");
      }

      const url = apiUrl(`/api/scenarios?pool_code=${encodeURIComponent(poolCode)}`);
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete scenario (${response.status})`);
      }
    },
    onSuccess: () => {
      lastSavedRef.current = "";
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Unknown error");
    },
  });

  const loadScenario = useCallback(async (): Promise<Map<string, MatchupPrediction> | null> => {
    if (!canLoad) return null;
    setError(null);

    const result = await scenarioQuery.refetch();
    if (result.error) {
      setError(result.error instanceof Error ? result.error.message : "Unknown error");
      return null;
    }

    const mapped = toPredictionMap(result.data ?? null);
    if (mapped) {
      const serialized = JSON.stringify(
        Array.from(mapped.entries()).map(([seriesCode, pred]) => ({
          seriesCode,
          winnerTeamCode: pred.winnerTeamCode,
          gamesWon: pred.gamesWon,
          ...(pred.bestThirdRank ? { bestThirdRank: pred.bestThirdRank } : {}),
        })),
      );
      lastSavedRef.current = serialized;
    } else {
      lastSavedRef.current = "";
    }
    return mapped;
  }, [canLoad, scenarioQuery]);

  const saveScenario = useCallback(
    async (predictions: Map<string, MatchupPrediction>) => {
      if (!canLoad) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        const predictionsArray = Array.from(predictions.entries()).map(([seriesCode, pred]) => ({
          seriesCode,
          winnerTeamCode: pred.winnerTeamCode,
          gamesWon: pred.gamesWon,
          ...(pred.bestThirdRank ? { bestThirdRank: pred.bestThirdRank } : {}),
        }));

        const serialized = JSON.stringify(predictionsArray);
        if (serialized === lastSavedRef.current) {
          return;
        }

        setError(null);
        void saveMutation.mutateAsync(predictionsArray).then(() => {
          lastSavedRef.current = serialized;
        });
      }, 500);
    },
    [canLoad, saveMutation],
  );

  const deleteScenario = useCallback(async () => {
    if (!canLoad) return;
    setError(null);
    await deleteMutation.mutateAsync();
  }, [canLoad, deleteMutation]);

  useEffect(
    () => () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    },
    [],
  );

  const derivedError = useMemo(() => {
    if (error) return error;
    if (scenarioQuery.error instanceof Error) return scenarioQuery.error.message;
    if (saveMutation.error instanceof Error) return saveMutation.error.message;
    if (deleteMutation.error instanceof Error) return deleteMutation.error.message;
    return null;
  }, [deleteMutation.error, error, saveMutation.error, scenarioQuery.error]);

  return {
    loadScenario,
    saveScenario,
    deleteScenario,
    isLoading: scenarioQuery.isFetching,
    isSaving: saveMutation.isPending,
    error: derivedError,
  };
}
